import { Box, Button, CircularProgress, CssBaseline, Stack, Typography } from '@mui/material';
import { BrowserRouter } from 'react-router';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import AppRouter from './appRouter';
import LoginForm from './pages/login/login';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import { LoggedInUser } from './models/user';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { DeviceProvider } from './pages/devices/deviceContext';
import { initWebSocket } from './websocket';
import theme from './theme';

const rootElement = document.getElementById('root');

if (!rootElement) throw new Error('Root element not found.');

const root = ReactDOM.createRoot(rootElement);

function App() {
  const [user, setUser] = React.useState<LoggedInUser | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [rememberMe, setRememberMe] = React.useState(false);
  const [savedEmail, setSavedEmail] = React.useState('');
  const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);

  React.useEffect(() => {
    const getSessionIdFromCookies = () => {
      const cookies = document.cookie.split('; ');
      const sessionCookie = cookies.find(cookie => cookie.startsWith('SessionId='));
      return sessionCookie ? sessionCookie.split('=')[1] : null;
    };

    const sessionId = getSessionIdFromCookies();
    if (sessionId) {
      fetch('/api/login', {
        method: 'POST',
        body: JSON.stringify({ sessionId }),
        headers: {
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch session');
          }
          return response.json();
        })
        .then((user: LoggedInUser) => {
          document.cookie = `SessionId=${user.sessionId}; path=/`;
          setUser(user);
          setLoading(false);
          setRememberMe(true);
          initWebSocket();
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }

    const emailCookie = document.cookie
      .split('; ')
      .find(cookie => cookie.startsWith('SavedEmail='));
    if (emailCookie) {
      setSavedEmail(emailCookie.split('=')[1]);
    }
  }, []);

  const handleLogin = (user: LoggedInUser) => {
    document.cookie = `SessionId=${user.sessionId}; path=/`;
    if (rememberMe) {
      document.cookie = `SavedEmail=${user.email}; path=/`;
    } else {
      document.cookie = 'SavedEmail=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
    }
    setUser(user);
    initWebSocket();
  };

  const wrap = (node: React.ReactNode): React.ReactNode => (
    <BrowserRouter>
      <DeviceProvider>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
              <title>IoT Devices Management</title>
              <Box sx={{
                py: 1,
                px: { xs: 1, sm: 2 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '3px solid',
                borderColor: 'primary.main',
                backgroundColor: '#e7d2d2',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                minHeight: '50px'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0 }}>
                  <img
                    src="https://ctrnd.com/wp-content/uploads/2024/02/Cortexrd-logo-2.svg"
                    alt="Cortex RD"
                    style={{ height: '32px', width: 'auto' }}
                  />
                  <Box sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0, color: '#a32b2b', fontSize: { xs: '0.95rem', sm: '1.25rem' }, whiteSpace: 'nowrap' }}>
                      IoT Devices Management
                      <Typography component="span" sx={{ fontSize: '0.7rem', ml: 0.5, color: '#9b5e5e', fontWeight: 400 }}>
                        v{process.env.APP_VERSION}
                      </Typography>
                    </Typography>
                    {user && (
                      <Typography
                        variant="caption"
                        onClick={() => {
                          if (window.confirm('Logout?')) {
                            document.cookie = 'SessionId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
                            window.location.reload();
                          }
                        }}
                        sx={{ color: '#9b5e5e', lineHeight: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {user.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {user && (
                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, flexShrink: 0 }}>
                    <Button
                      variant="text"
                      color="secondary"
                      size="small"
                      onClick={() => setChangePasswordOpen(true)}
                    >
                      Password
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        document.cookie = 'SessionId=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;';
                        window.location.reload();
                      }}
                    >
                      Logout
                    </Button>
                  </Box>
                )}
                <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
              </Box>
              {node}
            </Box>
          </ThemeProvider>
        </StyledEngineProvider>
      </DeviceProvider>
    </BrowserRouter>
  );

  if (loading) {
    return wrap((
      <Box sx={{ p: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading...</Typography>
      </Box>));
  }

  return wrap(
    user ? (
      <AppRouter user={user} />
    ) : (
      <Stack sx={{ height: '80vh' }} alignItems={'center'} justifyContent={'center'}>
        <Box sx={{
          p: 4,
          border: '2px solid',
          borderColor: 'primary.dark',
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.8)',
          width: '100%',
          maxWidth: 400,
          backgroundColor: 'background.paper'
        }}>
          <LoginForm
            onLogin={handleLogin}
            savedEmail={savedEmail}
            onRememberMeChange={setRememberMe}
          />
        </Box>
      </Stack >
    )
  );
}

root.render(<App />);